import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import api from "@/lib/api";
import { parseCapacityTiers } from "@/lib/capacityScope";

/**
 * Hooks for the org-wide capacity settings backend
 * (`/api/v1/capacity-v2/settings` and `/pto`).
 *
 * The endpoint prefix is still `/capacity-v2/*` for historical reasons,
 * but these are the only "v2" surfaces still in use after the v2 engine
 * was retired in favor of v1. They drive the Settings → Capacity Settings
 * page (org phase weights, per-person overhead %, PTO entries) — values
 * that v1 reads at request time.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrgSetting {
  key: string;
  value: string;          // raw stored value
  typed_value: number | string | boolean;
  value_type: "float" | "int" | "bool" | "str";
  label: string | null;
  description: string | null;
}

export interface PtoEntry {
  id: number;
  person_name: string;
  start_date: string;     // ISO yyyy-mm-dd
  end_date: string;
  hours_per_day: number | null;
  note: string | null;
  created_at: string | null;
}

export interface PtoEntryCreate {
  person_name: string;
  start_date: string;
  end_date: string;
  hours_per_day?: number | null;
  note?: string | null;
}

// ---------------------------------------------------------------------------
// Org capacity settings
// ---------------------------------------------------------------------------

export function useOrgCapacitySettings() {
  return useQuery<OrgSetting[]>({
    queryKey: ["capacity-rebuild", "settings"],
    queryFn: () => api.get("/capacity-v2/settings").then((r) => r.data),
    staleTime: 30_000,
  });
}

/** The board bands whose hours count toward capacity (2026-10-06).
 *  Falls back to Top 10 only until the settings load. */
export function useCapacityTiers(): Set<string> {
  const q = useOrgCapacitySettings();
  const raw = q.data?.find((s) => s.key === "capacity_tiers")?.value;
  return useMemo(() => parseCapacityTiers(raw), [raw]);
}

export function useUpdateOrgCapacitySetting() {
  const qc = useQueryClient();
  return useMutation<OrgSetting, Error, { key: string; value: string }>({
    mutationFn: ({ key, value }) =>
      api
        .put(`/capacity-v2/settings/${encodeURIComponent(key)}`, { value })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capacity-rebuild", "settings"] });
      // Phase weights / overhead changes affect every capacity calc — nuke
      // the v1 caches so the next /capacity render reads fresh values.
      qc.invalidateQueries({ queryKey: ["capacity"] });
    },
  });
}

// ---------------------------------------------------------------------------
// PTO
// ---------------------------------------------------------------------------

export function usePtoEntries(personName?: string) {
  return useQuery<PtoEntry[]>({
    queryKey: ["capacity-rebuild", "pto", personName ?? ""],
    queryFn: () =>
      api
        .get("/capacity-v2/pto", {
          params: personName ? { person_name: personName } : undefined,
        })
        .then((r) => r.data),
    staleTime: 30_000,
  });
}

export function useCreatePto() {
  const qc = useQueryClient();
  return useMutation<PtoEntry, Error, PtoEntryCreate>({
    mutationFn: (payload) =>
      api.post("/capacity-v2/pto", payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capacity-rebuild", "pto"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
    },
  });
}

export function useDeletePto() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) =>
      api.delete(`/capacity-v2/pto/${id}`).then(() => undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capacity-rebuild", "pto"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
    },
  });
}
